// frontend/src/components/admin/CompteRenduForm.js
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CompteRenduForm.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CompteRenduForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assembleeId = searchParams.get('assemblee');
  const isEdit = Boolean(id);

  // État du formulaire
  const [formData, setFormData] = useState({
    lieu: '',
    date_reunion: '',
    heure_prevue: '14:00',
    heure_debut_reelle: '',
    president_seance: '',
    priere_par: '',
    points_ordre_jour: [],
    annonces: [],
    participants: [],
    cotisations: [],
    tontine_hote_nom: '',
    tontine_co_hote_nom: '',
    dettes_cumulees: {},
    dette_totale: 0,
    emprunts_en_cours: [],
    etat_caisse: [],
    releve_bancaire: [],
    solde_banque: 0,
    evenement_nom: '',
    evenement_cotisations: [],
    points_divers: [],
    prochaines_reunions: [],
    observations: ''
  });

  const [membres, setMembres] = useState([]);
  const [etapeActive, setEtapeActive] = useState(1);
  // Membres affichés dans la section Adhésion (en plus de ceux ayant déjà un montant > 0)
  const [adhesionsAjoutees, setAdhesionsAjoutees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Solde de caisse de clôture de l'année précédente (dernier compte-rendu
  // daté de l'année N-1) — le tableau "État de Caisse" repart de zéro chaque
  // année, donc le vrai solde courant = ce report + entrées - sorties de
  // l'année en cours, pas juste entrées - sorties.
  const [soldeDepartCaisse, setSoldeDepartCaisse] = useState(0);
  const [assemblees, setAssemblees] = useState([]);

  // Liste des assemblées pour le sélecteur "Assemblée liée" — sans lien, le
  // bouton "Recharger les entrées du jour" (cotisations/tontine/etc.) ne peut
  // pas s'afficher, et les données doivent alors être ressaisies à la main.
  useEffect(() => {
    const chargerAssemblees = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/assemblees`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 100 }
        });
        setAssemblees(res.data?.data?.assemblees || res.data?.data || []);
      } catch (err) {
        console.error('Erreur chargement assemblées:', err);
      }
    };
    chargerAssemblees();
  }, []);

  useEffect(() => {
    const chargerSoldeReport = async () => {
      if (!formData.date_reunion) return;
      const anneeCourante = parseInt(formData.date_reunion.slice(0, 4), 10);
      if (!anneeCourante) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/comptes-rendus`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { annee: anneeCourante - 1, limit: 1, page: 1 }
        });
        const dernierCrAnneePrecedente = res.data?.data?.comptes_rendus?.[0];
        setSoldeDepartCaisse(parseFloat(dernierCrAnneePrecedente?.solde_caisse) || 0);
      } catch (err) {
        console.error('Erreur chargement solde de report:', err);
        setSoldeDepartCaisse(0);
      }
    };
    chargerSoldeReport();
  }, [formData.date_reunion]);

  // Charger les membres et le compte rendu
  useEffect(() => {
    chargerMembres();
    if (isEdit) {
      chargerCompteRendu();
    } else if (assembleeId) {
      chargerDonneesAssemblee(assembleeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assembleeId]);

  // Si en mode édition et que participants était vide dans le CR sauvegardé,
  // on reconstruit la liste depuis les membres chargés (tous marqués absents)
  useEffect(() => {
    if (isEdit && formData._participantsVides && membres.length > 0) {
      console.log('⚠️ Participants vides en édition → reconstruction depuis membres');
      const participantsReconstruits = membres.map(membre => ({
        membre_id: membre.id,
        nom: `${membre.prenom} ${membre.nom}`,
        present: false,
        heure_arrivee: null
      }));
      const cotisationsReconstruites = membres.map(membre => ({
        membre_id: membre.id,
        nom: `${membre.prenom} ${membre.nom}`,
        caisse: 0,
        maladie: 0,
        adhesion: 0,
        mariage: 0,
        tontine_hote: 0,
        tontine_co_hote: 0,
        total: 0
      }));
      setFormData(prev => ({
        ...prev,
        participants: participantsReconstruits,
        cotisations: prev.cotisations.length === 0 ? cotisationsReconstruites : prev.cotisations,
        _participantsVides: false
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membres, formData._participantsVides]);

  const chargerMembres = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/membres/actifs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const membresList = response.data.data || [];
      setMembres(membresList);
      
      // N'initialiser les participants que pour un nouveau CR sans assemblée_id
      // Si assemblée_id → chargerDonneesAssemblee() gère les participants
      // Si isEdit → chargerCompteRendu() gère les participants
      if (!isEdit && !assembleeId) {
        initialiserParticipants(membresList);
      }
    } catch (err) {
      console.error('Erreur chargement membres:', err);
      setError('Impossible de charger la liste des membres');
    }
  };

  // Helper : sépare un point d'ordre du jour par défaut "Douala / Français"
  // (ex: "Myenge na muka / Louanges et prière") en {titre_local, titre_fr}.
  // Si le séparateur " / " est absent (point ajouté manuellement), tout va
  // dans titre_fr, comme avant.
  const splitTitreOdj = (p) => {
    const idx = p.indexOf(' / ');
    if (idx === -1) return { titre_local: '', titre_fr: p };
    return { titre_local: p.slice(0, idx).trim(), titre_fr: p.slice(idx + 3).trim() };
  };

  // Helper : parse un champ JSON stocké en string ou retourne la valeur directement
  const parseJsonField = (field, defaultValue = []) => {
    if (!field) return defaultValue;
    if (Array.isArray(field) || typeof field === 'object') return field;
    try {
      const parsed = JSON.parse(field);
      return parsed !== null ? parsed : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };

  const chargerCompteRendu = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/comptes-rendus/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data.data || {};
      console.log('📥 Compte rendu chargé:', data);

      // Parser tous les champs JSON stockés en TEXT dans MySQL
      // Normaliser les participants : nom peut être 'nom' ou 'nom_complet' selon l'origine
      const rawParticipants = parseJsonField(data.participants, []);
      const participantsData = rawParticipants.map(p => ({
        ...p,
        // Normaliser : certains CR stockent 'nom_complet', d'autres 'nom'
        nom: p.nom || p.nom_complet || p.prenom_nom || `${p.prenom || ''} ${p.nom_complet || ''}`.trim() || '',
        present: p.present === true || p.present === 1
      }));

      // Si participants vide ou null -> initialiser depuis la liste des membres
      // On le fait APRÈS que chargerMembres() ait déjà chargé les membres
      setFormData({
        lieu: data.lieu || '',
        date_reunion: data.date_reunion
          ? (data.date_reunion.includes('T')
              ? data.date_reunion.split('T')[0]
              : data.date_reunion)
          : '',
        heure_prevue: data.heure_prevue ? data.heure_prevue.substring(0, 5) : '14:00',
        heure_debut_reelle: data.heure_debut_reelle ? data.heure_debut_reelle.substring(0, 5) : '',
        president_seance: data.president_seance || '',
        priere_par: data.priere_par || '',
        assemblee_id: data.assemblee_id || null,
        statut: data.statut || 'brouillon',
        observations: data.observations || '',
        points_ordre_jour: parseJsonField(data.points_ordre_jour, []),
        annonces: parseJsonField(data.annonces, []),
        participants: participantsData,
        cotisations: parseJsonField(data.cotisations, []),
        tontine_hote_nom: data.tontine_hote_nom || '',
        tontine_co_hote_nom: data.tontine_co_hote_nom || '',
        dettes_cumulees: parseJsonField(data.dettes_cumulees, {}),
        dette_totale: data.dette_totale || 0,
        emprunts_en_cours: parseJsonField(data.emprunts_en_cours, []),
        etat_caisse: parseJsonField(data.etat_caisse, []),
        releve_bancaire: parseJsonField(data.releve_bancaire, []),
        solde_banque: data.solde_banque || 0,
        evenement_nom: data.evenement_nom || '',
        evenement_cotisations: parseJsonField(data.evenement_cotisations, []),
        points_divers: parseJsonField(data.points_divers, []),
        prochaines_reunions: parseJsonField(data.prochaines_reunions, []),
        _participantsVides: participantsData.length === 0  // flag interne
      });
    } catch (err) {
      console.error('Erreur chargement compte rendu:', err);
      setError('Impossible de charger le compte rendu');
    } finally {
      setLoading(false);
    }
  };

  const chargerDonneesAssemblee = async (assembleeId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Récupérer participations ET assemblée en parallèle
      const [partResponse, assembleeResponse] = await Promise.all([
        axios.get(`${API_URL}/assemblees/${assembleeId}/participations`, { headers }),
        axios.get(`${API_URL}/assemblees/${assembleeId}`, { headers })
      ]);

      // ─── Données de l'assemblée ───
      const assembleeData = assembleeResponse.data.data;
      // La route getById retourne soit { assemblee, participants } soit directement l'objet
      const assemblee = assembleeData.assemblee || assembleeData;

      // ─── Participations : présents ET absents ───
      if (partResponse.data.success) {
        const { participations } = partResponse.data.data;

        console.log(`📋 Participations chargées: ${participations.length} membres`);
        console.log(`   Présents: ${participations.filter(p => p.present).length}`);
        console.log(`   Absents:  ${participations.filter(p => !p.present).length}`);

        // Construire la liste COMPLÈTE avec présents ET absents
        const participantsList = participations.map(p => ({
          membre_id: p.membre_id,
          // ✅ Toujours stocker sous 'nom' pour cohérence avec la carte
          nom: p.nom_complet || p.nom || '',
          // present peut être 0/1 (int MySQL) ou true/false
          present: p.present === 1 || p.present === true,
          heure_arrivee: p.heure_arrivee || null
        }));

        const cotisationsList = participations.map(p => ({
          membre_id: p.membre_id,
          nom: p.nom_complet || p.nom || '',
          caisse: 0,
          maladie: 0,
          deuil: 0,
          adhesion: 0,
          excellence: 0,
          mariage: 0,
          tontine_hote: 0,
          tontine_co_hote: 0,
          total: 0
        }));

        const hoteNomComplet   = (assemblee.hote_prenom || assemblee.hote_nom)
          ? `${assemblee.hote_prenom || ''} ${assemblee.hote_nom || ''}`.trim()
          : '';
        const coHoteNomComplet = (assemblee.co_hote_prenom || assemblee.co_hote_nom)
          ? `${assemblee.co_hote_prenom || ''} ${assemblee.co_hote_nom || ''}`.trim()
          : '';

        const dateReunion = assemblee.date_assemblee
          ? (assemblee.date_assemblee.includes('T')
              ? assemblee.date_assemblee.split('T')[0]
              : assemblee.date_assemblee)
          : '';

        setFormData(prev => ({
          ...prev,
          assemblee_id: assembleeId,
          lieu: assemblee.lieu || '',
          date_reunion: dateReunion,
          participants: participantsList,
          cotisations: cotisationsList,
          tontine_hote_nom: hoteNomComplet,
          tontine_co_hote_nom: coHoteNomComplet,
          nombre_presents: participantsList.filter(p => p.present).length,
          nombre_absents: participantsList.filter(p => !p.present).length,
          points_ordre_jour: Array.isArray(assemblee.ordre_du_jour)
            ? assemblee.ordre_du_jour.map((p, i) =>
                typeof p === 'string'
                  ? { numero: i + 1, ...splitTitreOdj(p), contenu: '', sous_points: [] }
                  : { numero: i + 1, titre_fr: '', titre_local: '', contenu: '', sous_points: [], ...p }
              )
            : []
        }));

        console.log('✅ Formulaire initialisé depuis assemblée:', assemblee.lieu);

        // Pré-remplir automatiquement depuis les entrées réellement saisies ce jour-là
        chargerEntreesJour(assembleeId);
      } else {
        // Si l'API participations échoue, utiliser la liste membres déjà chargée
        console.warn('⚠️ API participations échouée, utilisation de la liste membres');
        const dateReunion = assemblee.date_assemblee
          ? (assemblee.date_assemblee.includes('T')
              ? assemblee.date_assemblee.split('T')[0]
              : assemblee.date_assemblee)
          : '';
        setFormData(prev => ({
          ...prev,
          assemblee_id: assembleeId,
          lieu: assemblee.lieu || '',
          date_reunion: dateReunion
          // participants reste [] -> le useEffect _participantsVides va reconstruire
        }));
      }
    } catch (err) {
      console.error('Erreur chargement données assemblée:', err);
      // Ne pas bloquer, initialiser quand même avec les membres déjà chargés
      setFormData(prev => ({
        ...prev,
        assemblee_id: assembleeId,
        _participantsVides: true
      }));
      setError('Impossible de charger les données de l\'assemblée depuis le serveur. La liste des membres a été utilisée.');
    } finally {
      setLoading(false);
    }
  };

  // Récupère les cotisations (caisse/maladie) et la tontine réellement
  // saisies pour cette assemblée, et les fusionne dans le formulaire — évite
  // de retaper à la main ce qui a déjà été enregistré dans les modules
  // Cotisations et Tontine.
  const chargerEntreesJour = async (assembleeIdCible) => {
    if (!assembleeIdCible) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/assemblees/${assembleeIdCible}/entrees-jour`, { headers });
      if (!response.data.success) return;

      const {
        cotisations: cotisParMembre, tontine: tontineParMembre, hote_nom, co_hote_nom,
        dette_totale, emprunts, comptes_bancaires, solde_banque_total,
        etat_caisse, evenement, evenement_cotisations
      } = response.data.data;

      setFormData(prev => {
        const cotisations = prev.cotisations.map(cot => {
          const entreeCot = cotisParMembre[cot.membre_id];
          const entreeTon = tontineParMembre[cot.membre_id];
          const caisse          = entreeCot ? (entreeCot.caisse     || 0) : cot.caisse;
          const maladie         = entreeCot ? (entreeCot.maladie    || 0) : cot.maladie;
          const deuil           = entreeCot ? (entreeCot.deuil      || 0) : (cot.deuil || 0);
          const adhesion        = entreeCot ? (entreeCot.adhesion   || 0) : cot.adhesion;
          const excellence      = entreeCot ? (entreeCot.excellence || 0) : (cot.excellence || 0);
          const mariage         = entreeCot ? (entreeCot.mariage    || 0) : (cot.mariage || 0);
          const tontine_hote    = entreeTon ? (entreeTon.hote    || 0) : cot.tontine_hote;
          const tontine_co_hote = entreeTon ? (entreeTon.co_hote || 0) : cot.tontine_co_hote;
          return {
            ...cot,
            caisse, maladie, deuil, adhesion, excellence, mariage, tontine_hote, tontine_co_hote,
            total: caisse + maladie + deuil + adhesion + excellence + mariage + tontine_hote + tontine_co_hote
          };
        });

        const emprunts_en_cours = (emprunts || []).map(e => ({
          membre: e.membre,
          montant_session_24_25: e.montant_emprunte,
          montant_session_25_26: e.montant_rembourse,
          total: e.total
        }));

        const releve_bancaire = (comptes_bancaires || []).map(c => ({
          compte: c.compte,
          banque: c.banque,
          solde: c.solde
        }));

        return {
          ...prev,
          cotisations,
          tontine_hote_nom: hote_nom || prev.tontine_hote_nom,
          tontine_co_hote_nom: co_hote_nom || '',
          dette_totale: dette_totale ?? prev.dette_totale,
          emprunts_en_cours: emprunts_en_cours.length > 0 ? emprunts_en_cours : prev.emprunts_en_cours,
          releve_bancaire: releve_bancaire.length > 0 ? releve_bancaire : prev.releve_bancaire,
          solde_banque: solde_banque_total ?? prev.solde_banque,
          // Fusion par mois (pas un remplacement complet) : un mois déjà présent
          // (saisi ou rechargé précédemment) reste intact — seul le mois de LA
          // RÉUNION EN COURS est rafraîchi automatiquement (c'est le seul où de
          // nouveaux paiements peuvent arriver pendant la rédaction du CR), et
          // seuls les mois totalement absents sont ajoutés. Sans ça, rouvrir un
          // brouillon écrasait les mois déjà clôturés/saisis à la main (ex.
          // entrées d'un mois où la synchro automatique n'existait pas encore).
          etat_caisse: (etat_caisse && etat_caisse.length > 0)
            ? (() => {
                // Même abréviations que le backend (assemblees.routes.js), pour
                // que la comparaison de mois soit fiable.
                const NOMS_MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                let moisReunion = null;
                if (prev.date_reunion) {
                  const d = new Date(prev.date_reunion);
                  moisReunion = `${NOMS_MOIS[d.getMonth()]} ${d.getFullYear()}`;
                }
                return etat_caisse.map(ligneFraiche => {
                  const ligneExistante = prev.etat_caisse.find(l => l.mois === ligneFraiche.mois);
                  if (!ligneExistante) return ligneFraiche;
                  const estMoisDeLaReunion = ligneFraiche.mois === moisReunion;
                  return estMoisDeLaReunion ? { ...ligneFraiche, commentaire: ligneExistante.commentaire } : ligneExistante;
                });
              })()
            : prev.etat_caisse,
          evenement_nom: evenement ? evenement.nom : prev.evenement_nom,
          evenement_cotisations: (evenement_cotisations && evenement_cotisations.length > 0) ? evenement_cotisations : prev.evenement_cotisations
        };
      });
    } catch (err) {
      console.error('Erreur chargement entrées du jour:', err);
      setError("Impossible de récupérer les entrées du jour depuis les modules Cotisations/Tontine");
    }
  };

  const [rechargementEnCours, setRechargementEnCours] = useState(false);

  // Rafraîchit manuellement les sections auto-remplies (cotisations, tontine,
  // dette, emprunts, comptes bancaires, état de caisse, événement) sur un
  // brouillon déjà créé — sans ce bouton, un brouillon ouvert avant que de
  // nouveaux paiements soient enregistrés reste figé sur les anciennes valeurs.
  const rechargerEntreesDuJour = async () => {
    if (!formData.assemblee_id) return;
    setRechargementEnCours(true);
    setError('');
    try {
      await chargerNouveauxMembres();
      await chargerEntreesJour(formData.assemblee_id);
      alert('Entrées du jour rechargées !');
    } finally {
      setRechargementEnCours(false);
    }
  };

  // Ajoute au formulaire les membres actifs créés après l'ouverture du CR
  // (ex: nouvelle adhésion enregistrée pendant la réunion) — sans toucher
  // aux lignes déjà présentes pour ne pas écraser la saisie en cours.
  const chargerNouveauxMembres = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/membres/actifs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const membresList = response.data.data || [];
      setMembres(membresList);

      setFormData(prev => {
        const idsExistants = new Set(prev.cotisations.map(c => c.membre_id));
        const nouveaux = membresList.filter(m => !idsExistants.has(m.id));
        if (nouveaux.length === 0) return prev;

        const nouveauxParticipants = nouveaux.map(m => ({
          membre_id: m.id, nom: `${m.nom} ${m.prenom}`, present: false, heure_arrivee: null
        }));
        const nouvellesCotisations = nouveaux.map(m => ({
          membre_id: m.id, nom: `${m.nom} ${m.prenom}`,
          caisse: 0, maladie: 0, deuil: 0, adhesion: 0, mariage: 0,
          tontine_hote: 0, tontine_co_hote: 0, total: 0
        }));

        return {
          ...prev,
          participants: [...prev.participants, ...nouveauxParticipants],
          cotisations: [...prev.cotisations, ...nouvellesCotisations],
        };
      });
    } catch (err) {
      console.error('Erreur chargement nouveaux membres:', err);
    }
  };

  const initialiserParticipants = (membresList) => {
    const participants = membresList.map(membre => ({
      membre_id: membre.id,
      nom: `${membre.nom} ${membre.prenom}`,
      present: false,
      heure_arrivee: null
    }));

    const cotisations = membresList.map(membre => ({
      membre_id: membre.id,
      nom: `${membre.nom} ${membre.prenom}`,
      caisse: 0,
      maladie: 0,
      deuil: 0,
      adhesion: 0,
      mariage: 0,
      tontine_hote: 0,
      tontine_co_hote: 0,
      total: 0
    }));

    setFormData(prev => ({
      ...prev,
      participants,
      cotisations
    }));
  };

  // Gestion des changements de base
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gestion des participants
  const togglePresence = (index) => {
    const newParticipants = [...formData.participants];
    newParticipants[index].present = !newParticipants[index].present;
    if (newParticipants[index].present && !newParticipants[index].heure_arrivee) {
      const now = new Date();
      newParticipants[index].heure_arrivee = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    }
    setFormData(prev => ({
      ...prev,
      participants: newParticipants
    }));
  };

  // Gestion des annonces
  const ajouterAnnonce = () => {
    setFormData(prev => ({
      ...prev,
      annonces: [...prev.annonces, { membre: '', type: 'info', message: '' }]
    }));
  };

  const modifierAnnonce = (index, field, value) => {
    const newAnnonces = [...formData.annonces];
    newAnnonces[index][field] = value;
    setFormData(prev => ({
      ...prev,
      annonces: newAnnonces
    }));
  };

  const supprimerAnnonce = (index) => {
    setFormData(prev => ({
      ...prev,
      annonces: prev.annonces.filter((_, i) => i !== index)
    }));
  };

  // Gestion des points à l'ordre du jour
  const ajouterPoint = () => {
    setFormData(prev => ({
      ...prev,
      points_ordre_jour: [
        ...prev.points_ordre_jour,
        {
          numero: prev.points_ordre_jour.length + 1,
          titre_fr: '',
          titre_local: '',
          contenu: '',
          sous_points: []
        }
      ]
    }));
  };

  const modifierPoint = (index, field, value) => {
    const newPoints = [...formData.points_ordre_jour];
    newPoints[index][field] = value;
    setFormData(prev => ({
      ...prev,
      points_ordre_jour: newPoints
    }));
  };

  const supprimerPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      points_ordre_jour: prev.points_ordre_jour.filter((_, i) => i !== index)
    }));
  };

  // Gestion des cotisations (caisse/maladie/adhésion + tontine versée au
  // récipiendaire et, s'il y en a un, au co-hôte du jour)
  const modifierCotisation = (index, field, value) => {
    const newCotisations = [...formData.cotisations];
    newCotisations[index][field] = parseFloat(value) || 0;

    // Recalculer le total
    newCotisations[index].total =
      newCotisations[index].caisse +
      newCotisations[index].maladie +
      (newCotisations[index].deuil || 0) +
      newCotisations[index].adhesion +
      (newCotisations[index].excellence || 0) +
      (newCotisations[index].mariage || 0) +
      (newCotisations[index].tontine_hote || 0) +
      (newCotisations[index].tontine_co_hote || 0);

    setFormData(prev => ({
      ...prev,
      cotisations: newCotisations
    }));
  };

  // Gestion de l'état de caisse
  const ajouterLigneCaisse = () => {
    const moisActuels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const now = new Date();
    setFormData(prev => ({
      ...prev,
      etat_caisse: [
        ...prev.etat_caisse,
        {
          mois: `${moisActuels[now.getMonth()]} ${now.getFullYear()}`,
          entrees: 0,
          sorties: 0,
          commentaire: ''
        }
      ]
    }));
  };

  const modifierLigneCaisse = (index, field, value) => {
    const newCaisse = [...formData.etat_caisse];
    newCaisse[index][field] = field === 'mois' || field === 'commentaire'
      ? value
      : parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, etat_caisse: newCaisse }));
  };

  const supprimerLigneCaisse = (index) => {
    setFormData(prev => ({
      ...prev,
      etat_caisse: prev.etat_caisse.filter((_, i) => i !== index)
    }));
  };

  // Gestion des emprunts en cours
  const ajouterEmprunt = () => {
    setFormData(prev => ({
      ...prev,
      emprunts_en_cours: [
        ...prev.emprunts_en_cours,
        { membre: '', montant_session_24_25: 0, montant_session_25_26: 0, total: 0 }
      ]
    }));
  };

  const modifierEmprunt = (index, field, value) => {
    const newEmprunts = [...formData.emprunts_en_cours];
    if (field === 'membre') {
      newEmprunts[index].membre = value;
    } else {
      newEmprunts[index][field] = parseFloat(value) || 0;
      newEmprunts[index].total = Math.max(0,
        (newEmprunts[index].montant_session_24_25 || 0) -
        (newEmprunts[index].montant_session_25_26 || 0)
      );
    }
    setFormData(prev => ({ ...prev, emprunts_en_cours: newEmprunts }));
  };

  const supprimerEmprunt = (index) => {
    setFormData(prev => ({
      ...prev,
      emprunts_en_cours: prev.emprunts_en_cours.filter((_, i) => i !== index)
    }));
  };

  // Gestion des comptes bancaires
  const ajouterLigneBancaire = () => {
    setFormData(prev => ({
      ...prev,
      releve_bancaire: [...prev.releve_bancaire, { compte: '', banque: '', solde: 0 }]
    }));
  };

  const modifierLigneBancaire = (index, field, value) => {
    const nouvelles = [...formData.releve_bancaire];
    nouvelles[index][field] = field === 'solde' ? (parseFloat(value) || 0) : value;
    const solde_banque = nouvelles.reduce((s, c) => s + (parseFloat(c.solde) || 0), 0);
    setFormData(prev => ({ ...prev, releve_bancaire: nouvelles, solde_banque }));
  };

  const supprimerLigneBancaire = (index) => {
    const nouvelles = formData.releve_bancaire.filter((_, i) => i !== index);
    const solde_banque = nouvelles.reduce((s, c) => s + (parseFloat(c.solde) || 0), 0);
    setFormData(prev => ({ ...prev, releve_bancaire: nouvelles, solde_banque }));
  };

  // Calculer totaux caisse
  const calculerTotauxCaisse = () => {
    return formData.etat_caisse.reduce(
      (acc, ligne) => ({
        entrees: acc.entrees + (parseFloat(ligne.entrees) || 0),
        sorties: acc.sorties + (parseFloat(ligne.sorties) || 0)
      }),
      { entrees: 0, sorties: 0 }
    );
  };

  // Gestion des points divers
  const ajouterPointDivers = () => {
    setFormData(prev => ({
      ...prev,
      points_divers: [...prev.points_divers, { auteur: '', sujet: '', contenu: '' }]
    }));
  };

  const modifierPointDivers = (index, field, value) => {
    const newPoints = [...formData.points_divers];
    newPoints[index][field] = value;
    setFormData(prev => ({
      ...prev,
      points_divers: newPoints
    }));
  };

  const supprimerPointDivers = (index) => {
    setFormData(prev => ({
      ...prev,
      points_divers: prev.points_divers.filter((_, i) => i !== index)
    }));
  };

  // Gestion des prochaines réunions
  const ajouterProchaineReunion = () => {
    setFormData(prev => ({
      ...prev,
      prochaines_reunions: [
        ...prev.prochaines_reunions,
        { mois: '', date: '', heure: '12:00', lieu: '' }
      ]
    }));
  };

  const modifierProchaineReunion = (index, field, value) => {
    const newReunions = [...formData.prochaines_reunions];
    newReunions[index][field] = value;
    setFormData(prev => ({
      ...prev,
      prochaines_reunions: newReunions
    }));
  };

  const supprimerProchaineReunion = (index) => {
    setFormData(prev => ({
      ...prev,
      prochaines_reunions: prev.prochaines_reunions.filter((_, i) => i !== index)
    }));
  };

  // Calculer les totaux
  const calculerTotaux = () => {
    const totaux = {
      total_caisse: 0,
      total_maladie: 0,
      total_deuil: 0,
      total_adhesion: 0,
      total_excellence: 0,
      total_mariage: 0,
      total_tontine_hote: 0,
      total_tontine_co_hote: 0,
      total_general: 0
    };

    formData.cotisations.forEach(cot => {
      totaux.total_caisse += cot.caisse;
      totaux.total_maladie += cot.maladie;
      totaux.total_deuil += (cot.deuil || 0);
      totaux.total_adhesion += cot.adhesion;
      totaux.total_excellence += (cot.excellence || 0);
      totaux.total_mariage += (cot.mariage || 0);
      totaux.total_tontine_hote += (cot.tontine_hote || 0);
      totaux.total_tontine_co_hote += (cot.tontine_co_hote || 0);
      totaux.total_general += cot.total;
    });

    return totaux;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const totauxCaisse = calculerTotauxCaisse();
      const dataToSubmit = {
        ...formData,
        assemblee_id: formData.assemblee_id || null,
        solde_caisse: soldeDepartCaisse + totauxCaisse.entrees - totauxCaisse.sorties
      };
      
      if (isEdit) {
        await axios.put(`${API_URL}/comptes-rendus/${id}`, dataToSubmit, config);
        alert('Compte rendu mis à jour avec succès!');
      } else {
        await axios.post(`${API_URL}/comptes-rendus`, dataToSubmit, config);
        alert('Compte rendu créé avec succès!');
      }

      navigate('/admin/comptes-rendus');
    } catch (err) {
      console.error('Erreur soumission:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder comme brouillon
  const sauvegarderBrouillon = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const totauxCaisseBrouillon = calculerTotauxCaisse();
      const dataToSave = {
        ...formData,
        statut: 'brouillon',
        solde_caisse: soldeDepartCaisse + totauxCaisseBrouillon.entrees - totauxCaisseBrouillon.sorties
      };

      if (isEdit) {
        await axios.put(`${API_URL}/comptes-rendus/${id}`, dataToSave, config);
      } else {
        const response = await axios.post(`${API_URL}/comptes-rendus`, dataToSave, config);
        navigate(`/admin/comptes-rendus/modifier/${response.data.data.id}`);
      }

      alert('Brouillon sauvegardé!');
    } catch (err) {
      console.error('Erreur sauvegarde brouillon:', err);
      setError('Erreur lors de la sauvegarde du brouillon');
    } finally {
      setLoading(false);
    }
  };

  const totaux = calculerTotaux();

  if (loading && isEdit) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="compte-rendu-form">
      <div className="form-header">
        <h1>{isEdit ? 'Modifier' : 'Nouveau'} Compte Rendu</h1>
        {formData.assemblee_id && (
          <button
            type="button"
            className="btn-outline"
            onClick={rechargerEntreesDuJour}
            disabled={rechargementEnCours}
            title="Recharger les cotisations, tontine, dette, emprunts, comptes bancaires, état de caisse et événement depuis les modules à jour"
          >
            {rechargementEnCours ? '⏳ Rechargement...' : '🔄 Recharger les entrées du jour'}
          </button>
        )}
        <div className="form-navigation">
          <button
            type="button"
            className={`nav-btn ${etapeActive === 1 ? 'active' : ''}`}
            onClick={() => setEtapeActive(1)}
          >
            1. Infos Générales
          </button>
          <button
            type="button"
            className={`nav-btn ${etapeActive === 2 ? 'active' : ''}`}
            onClick={() => setEtapeActive(2)}
          >
            2. Participants
          </button>
          <button
            type="button"
            className={`nav-btn ${etapeActive === 3 ? 'active' : ''}`}
            onClick={() => setEtapeActive(3)}
          >
            3. Points & Annonces
          </button>
          <button
            type="button"
            className={`nav-btn ${etapeActive === 4 ? 'active' : ''}`}
            onClick={() => setEtapeActive(4)}
          >
            4. Cotisations
          </button>
          <button
            type="button"
            className={`nav-btn ${etapeActive === 5 ? 'active' : ''}`}
            onClick={() => setEtapeActive(5)}
          >
            5. Divers
          </button>
          <button
            type="button"
            className={`nav-btn ${etapeActive === 6 ? 'active' : ''}`}
            onClick={() => setEtapeActive(6)}
          >
            6. Finances
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="form-content">
        {/* ÉTAPE 1: Informations Générales */}
        {etapeActive === 1 && (
          <div className="form-section">
            <h2>Informations de la Réunion</h2>
            
            <div className="form-group">
              <label htmlFor="lieu">Lieu de la réunion *</label>
              <input
                type="text"
                id="lieu"
                name="lieu"
                value={formData.lieu}
                onChange={handleChange}
                required
                placeholder="Ex: AKWA NORD Chez MME EKE Epse WANGA Armande Aurore"
              />
            </div>

            <div className="form-group">
              <label htmlFor="assemblee_id">Assemblée liée</label>
              <select
                id="assemblee_id"
                name="assemblee_id"
                value={formData.assemblee_id || ''}
                onChange={handleChange}
              >
                <option value="">— Aucune —</option>
                {assemblees.map(a => (
                  <option key={a.id} value={a.id}>
                    {new Date(a.date_assemblee).toLocaleDateString('fr-FR')} — {a.lieu || 'Lieu non précisé'}
                  </option>
                ))}
              </select>
              <small className="section-hint">
                Sans assemblée liée, impossible de recharger automatiquement les cotisations,
                la tontine, les dettes et l'état de caisse depuis les modules à jour.
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date_reunion">Date *</label>
                <input
                  type="date"
                  id="date_reunion"
                  name="date_reunion"
                  value={formData.date_reunion}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="heure_prevue">Heure prévue *</label>
                <input
                  type="time"
                  id="heure_prevue"
                  name="heure_prevue"
                  value={formData.heure_prevue}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="heure_debut_reelle">Heure début réelle</label>
                <input
                  type="time"
                  id="heure_debut_reelle"
                  name="heure_debut_reelle"
                  value={formData.heure_debut_reelle}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="president_seance">Président de séance</label>
                <select
                  id="president_seance"
                  name="president_seance"
                  value={formData.president_seance}
                  onChange={handleChange}
                >
                  <option value="">Sélectionner...</option>
                  {membres.map(membre => (
                    <option key={membre.id} value={`${membre.nom} ${membre.prenom}`}>
                      {membre.nom} {membre.prenom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="priere_par">Prière dirigée par</label>
                <select
                  id="priere_par"
                  name="priere_par"
                  value={formData.priere_par}
                  onChange={handleChange}
                >
                  <option value="">Sélectionner...</option>
                  {membres.map(membre => (
                    <option key={membre.id} value={`${membre.nom} ${membre.prenom}`}>
                      {membre.nom} {membre.prenom}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={() => setEtapeActive(2)}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 2: Participants */}
        {etapeActive === 2 && (
          <div className="form-section">
            <h2>Participants</h2>
            <p className="section-description">
              Cliquez sur un membre pour marquer sa présence/absence
            </p>

            {formData.participants.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                <p>⏳ Chargement des membres...</p>
              </div>
            )}
            <div className="participants-grid">
              {formData.participants.map((participant, index) => (
                <div
                  key={participant.membre_id}
                  className={`participant-card ${participant.present ? 'present' : 'absent'}`}
                  onClick={() => togglePresence(index)}
                >
                  <div className="participant-nom">{participant.nom || participant.nom_complet || participant.prenom_nom || '—'}</div>
                  <div className="participant-statut">
                    {participant.present ? (
                      <>
                        <span className="status-icon">✓</span>
                        <span>Présent(e)</span>
                      </>
                    ) : (
                      <>
                        <span className="status-icon">✗</span>
                        <span>Absent(e)</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="participants-summary">
              <div className="summary-item">
                <strong>Présents:</strong> {formData.participants.filter(p => p.present).length}
              </div>
              <div className="summary-item">
                <strong>Absents:</strong> {formData.participants.filter(p => !p.present).length}
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setEtapeActive(1)}>
                Précédent
              </button>
              <button type="button" className="btn-primary" onClick={() => setEtapeActive(3)}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 3: Points à l'ordre du jour et Annonces */}
        {etapeActive === 3 && (
          <div className="form-section">
            <h2>Points à l'Ordre du Jour</h2>
            
            {formData.points_ordre_jour.map((point, index) => (
              <div key={index} className="point-item">
                <div className="point-header">
                  <h3>Point {point.numero}</h3>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => supprimerPoint(index)}
                  >
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label>Titre (Français)</label>
                  <input
                    type="text"
                    value={point.titre_fr}
                    onChange={(e) => modifierPoint(index, 'titre_fr', e.target.value)}
                    placeholder="Ex: PREPARATION DE LA FETE ANNUELLE FAMILIALE 2025"
                  />
                </div>

                <div className="form-group">
                  <label>Titre (Langue locale)</label>
                  <input
                    type="text"
                    value={point.titre_local}
                    onChange={(e) => modifierPoint(index, 'titre_local', e.target.value)}
                    placeholder="Ex: MBOSAN MWA NDA LA NDOLO LA 2025"
                  />
                </div>

                <div className="form-group">
                  <label>Contenu</label>
                  <textarea
                    value={point.contenu}
                    onChange={(e) => modifierPoint(index, 'contenu', e.target.value)}
                    rows="4"
                    placeholder="Détails du point..."
                  />
                </div>
              </div>
            ))}

            <button type="button" className="btn-add" onClick={ajouterPoint}>
              + Ajouter un point
            </button>

            <h2 style={{ marginTop: '30px' }}>Annonces</h2>
            
            {formData.annonces.map((annonce, index) => (
              <div key={index} className="annonce-item">
                <div className="form-row">
                  <div className="form-group">
                    <label>Membre</label>
                    <select
                      value={annonce.membre}
                      onChange={(e) => modifierAnnonce(index, 'membre', e.target.value)}
                    >
                      <option value="">Sélectionner...</option>
                      {membres.map(membre => (
                        <option key={membre.id} value={`${membre.nom} ${membre.prenom}`}>
                          {membre.nom} {membre.prenom}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={annonce.type}
                      onChange={(e) => modifierAnnonce(index, 'type', e.target.value)}
                    >
                      <option value="info">Information</option>
                      <option value="felicitations">Félicitations</option>
                      <option value="condoleances">Condoléances</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => supprimerAnnonce(index)}
                  >
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={annonce.message}
                    onChange={(e) => modifierAnnonce(index, 'message', e.target.value)}
                    rows="2"
                    placeholder="Ex: Notre sœur NDONGO D. Linda nous annonce que leur maman est souffrante..."
                  />
                </div>
              </div>
            ))}

            <button type="button" className="btn-add" onClick={ajouterAnnonce}>
              + Ajouter une annonce
            </button>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setEtapeActive(2)}>
                Précédent
              </button>
              <button type="button" className="btn-primary" onClick={() => setEtapeActive(4)}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 4: Cotisations */}
        {etapeActive === 4 && (
          <div className="form-section">
            <h2>Cotisations</h2>

            {formData.assemblee_id && (
              <div className="form-actions" style={{ marginBottom: '1rem' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => chargerEntreesJour(formData.assemblee_id)}
                >
                  🔄 Récupérer depuis les entrées du jour
                </button>
                <span className="section-hint" style={{ marginLeft: '0.5rem' }}>
                  Pré-remplit caisse/maladie/deuil/adhésion/mariage et la tontine (récipiendaire{formData.tontine_co_hote_nom ? ' + co-hôte' : ''})
                  depuis ce qui a déjà été saisi dans les modules Cotisations et Tontine pour
                  cette assemblée — reste modifiable ci-dessous.
                </span>
              </div>
            )}

            {/* ── COTISATIONS (Caisse / Maladie / Deuil / Excellence scolaire) ── */}
            <div className="cotisations-table-container">
              <table className="cotisations-table">
                <thead>
                  <tr>
                    <th>Membre</th>
                    <th>Caisse</th>
                    <th>Maladie</th>
                    <th>Deuil</th>
                    <th>Excellence scolaire</th>
                    <th>Mariage</th>
                    <th>Sous-total</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.cotisations.map((cot, index) => (
                    <tr key={cot.membre_id}>
                      <td>{cot.nom}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.caisse}
                          onChange={(e) => modifierCotisation(index, 'caisse', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.maladie}
                          onChange={(e) => modifierCotisation(index, 'maladie', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.deuil || 0}
                          onChange={(e) => modifierCotisation(index, 'deuil', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.excellence || 0}
                          onChange={(e) => modifierCotisation(index, 'excellence', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.mariage || 0}
                          onChange={(e) => modifierCotisation(index, 'mariage', e.target.value)}
                        />
                      </td>
                      <td className="total-cell">
                        {(cot.caisse + cot.maladie + (cot.deuil || 0) + (cot.excellence || 0) + (cot.mariage || 0)).toLocaleString('fr-FR')} FCFA
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totaux-row">
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{totaux.total_caisse.toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{totaux.total_maladie.toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{totaux.total_deuil.toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{totaux.total_excellence.toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{totaux.total_mariage.toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{(totaux.total_caisse + totaux.total_maladie + totaux.total_deuil + totaux.total_excellence + totaux.total_mariage).toLocaleString('fr-FR')} FCFA</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ── TONTINE DU JOUR ── */}
            <h2 style={{ marginTop: '30px' }}>Tontine du Jour</h2>
            <p className="section-hint">Versements des membres au(x) récipiendaire(s) de la tontine ce jour.</p>
            <div className="cotisations-table-container">
              <table className="cotisations-table">
                <thead>
                  <tr>
                    <th>Membre</th>
                    <th>Tontine{formData.tontine_hote_nom ? ` (${formData.tontine_hote_nom})` : ''}</th>
                    {formData.tontine_co_hote_nom && (
                      <th>Tontine ({formData.tontine_co_hote_nom})</th>
                    )}
                    <th>Sous-total</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.cotisations.map((cot, index) => (
                    <tr key={cot.membre_id}>
                      <td>{cot.nom}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={cot.tontine_hote}
                          onChange={(e) => modifierCotisation(index, 'tontine_hote', e.target.value)}
                        />
                      </td>
                      {formData.tontine_co_hote_nom && (
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={cot.tontine_co_hote}
                            onChange={(e) => modifierCotisation(index, 'tontine_co_hote', e.target.value)}
                          />
                        </td>
                      )}
                      <td className="total-cell">
                        {((cot.tontine_hote || 0) + (cot.tontine_co_hote || 0)).toLocaleString('fr-FR')} FCFA
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totaux-row">
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{totaux.total_tontine_hote.toLocaleString('fr-FR')} FCFA</strong></td>
                    {formData.tontine_co_hote_nom && (
                      <td><strong>{totaux.total_tontine_co_hote.toLocaleString('fr-FR')} FCFA</strong></td>
                    )}
                    <td><strong>{(totaux.total_tontine_hote + totaux.total_tontine_co_hote).toLocaleString('fr-FR')} FCFA</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ── NOUVELLES ADHÉSIONS ── */}
            <h2 style={{ marginTop: '30px' }}>Nouvelles Adhésions</h2>
            <p className="section-hint">Il n'y en a pas tous les mois — concerne uniquement les nouveaux membres adhérant ce jour-là.</p>

            {(() => {
              const idsAffiches = [...new Set([
                ...adhesionsAjoutees,
                ...formData.cotisations.filter(c => c.adhesion > 0).map(c => c.membre_id)
              ])];
              const lignesAdhesion = formData.cotisations
                .map((cot, index) => ({ cot, index }))
                .filter(({ cot }) => idsAffiches.includes(cot.membre_id));
              const membresSansAdhesion = formData.cotisations.filter(c => !idsAffiches.includes(c.membre_id));

              return (
                <>
                  {lignesAdhesion.length > 0 && (
                    <div className="cotisations-table-container">
                      <table className="cotisations-table">
                        <thead>
                          <tr>
                            <th>Membre</th>
                            <th>Montant adhésion (FCFA)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lignesAdhesion.map(({ cot, index }) => (
                            <tr key={cot.membre_id}>
                              <td>{cot.nom}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={cot.adhesion}
                                  onChange={(e) => modifierCotisation(index, 'adhesion', e.target.value)}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => {
                                    modifierCotisation(index, 'adhesion', 0);
                                    setAdhesionsAjoutees(prev => prev.filter(id => id !== cot.membre_id));
                                  }}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="totaux-row">
                            <td><strong>TOTAL</strong></td>
                            <td><strong>{totaux.total_adhesion.toLocaleString('fr-FR')} FCFA</strong></td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {membresSansAdhesion.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => {
                        const membreId = parseInt(e.target.value);
                        if (membreId) setAdhesionsAjoutees(prev => [...prev, membreId]);
                      }}
                      style={{ maxWidth: '300px' }}
                    >
                      <option value="">+ Ajouter une adhésion...</option>
                      {membresSansAdhesion.map(c => (
                        <option key={c.membre_id} value={c.membre_id}>{c.nom}</option>
                      ))}
                    </select>
                  )}
                </>
              );
            })()}

            <div className="form-actions" style={{ marginTop: '30px' }}>
              <button type="button" className="btn-secondary" onClick={() => setEtapeActive(3)}>
                Précédent
              </button>
              <button type="button" className="btn-primary" onClick={() => setEtapeActive(5)}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 5: Points Divers et Prochaines Réunions */}
        {etapeActive === 5 && (
          <div className="form-section">
            <h2>Points Divers</h2>
            
            {formData.points_divers.map((point, index) => (
              <div key={index} className="point-divers-item">
                <div className="form-row">
                  <div className="form-group">
                    <label>Auteur</label>
                    <select
                      value={point.auteur}
                      onChange={(e) => modifierPointDivers(index, 'auteur', e.target.value)}
                    >
                      <option value="">Sélectionner...</option>
                      {membres.map(membre => (
                        <option key={membre.id} value={`${membre.nom} ${membre.prenom}`}>
                          {membre.nom} {membre.prenom}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => supprimerPointDivers(index)}
                  >
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label>Sujet</label>
                  <input
                    type="text"
                    value={point.sujet}
                    onChange={(e) => modifierPointDivers(index, 'sujet', e.target.value)}
                    placeholder="Sujet du point divers"
                  />
                </div>

                <div className="form-group">
                  <label>Contenu</label>
                  <textarea
                    value={point.contenu}
                    onChange={(e) => modifierPointDivers(index, 'contenu', e.target.value)}
                    rows="3"
                    placeholder="Détails..."
                  />
                </div>
              </div>
            ))}

            <button type="button" className="btn-add" onClick={ajouterPointDivers}>
              + Ajouter un point divers
            </button>

            <h2 style={{ marginTop: '30px' }}>Prochaines Réunions</h2>
            
            {formData.prochaines_reunions.map((reunion, index) => (
              <div key={index} className="prochaine-reunion-item">
                <div className="form-row">
                  <div className="form-group">
                    <label>Mois</label>
                    <input
                      type="text"
                      value={reunion.mois}
                      onChange={(e) => modifierProchaineReunion(index, 'mois', e.target.value)}
                      placeholder="Ex: Septembre 2025"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={reunion.date}
                      onChange={(e) => modifierProchaineReunion(index, 'date', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Heure</label>
                    <input
                      type="time"
                      value={reunion.heure}
                      onChange={(e) => modifierProchaineReunion(index, 'heure', e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => supprimerProchaineReunion(index)}
                  >
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label>Lieu</label>
                  <input
                    type="text"
                    value={reunion.lieu}
                    onChange={(e) => modifierProchaineReunion(index, 'lieu', e.target.value)}
                    placeholder="Ex: Chez Mme OBI Catherine..."
                  />
                </div>
              </div>
            ))}

            <button type="button" className="btn-add" onClick={ajouterProchaineReunion}>
              + Ajouter une réunion
            </button>

            <h2 style={{ marginTop: '30px' }}>Observations</h2>
            <div className="form-group">
              <textarea
                name="observations"
                value={formData.observations}
                onChange={handleChange}
                rows="4"
                placeholder="Observations générales..."
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setEtapeActive(4)}>
                Précédent
              </button>
              <button type="button" className="btn-primary" onClick={() => setEtapeActive(6)}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 6: Finances */}
        {etapeActive === 6 && (
          <div className="form-section">

            {/* ── ÉTAT DE CAISSE ── */}
            <h2>État de Caisse du Mois</h2>
            <p className="section-hint">Entrées (recettes) et sorties (dépenses approuvées) depuis janvier {new Date().getFullYear()} — pré-remplies automatiquement, modifiables si besoin.</p>

            {formData.etat_caisse.length > 0 && (
              <div className="table-responsive">
                <table className="cotisations-table">
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>Entrées (FCFA)</th>
                      <th>Sorties (FCFA)</th>
                      <th>Commentaires</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.etat_caisse.map((ligne, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={ligne.mois}
                            onChange={(e) => modifierLigneCaisse(index, 'mois', e.target.value)}
                            placeholder="Ex: Jan 2025"
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="1"
                            value={ligne.entrees}
                            onChange={(e) => modifierLigneCaisse(index, 'entrees', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="1"
                            value={ligne.sorties}
                            onChange={(e) => modifierLigneCaisse(index, 'sorties', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ligne.commentaire}
                            onChange={(e) => modifierLigneCaisse(index, 'commentaire', e.target.value)}
                            placeholder="Objet de l'entrée/sortie..."
                            style={{ width: '200px' }}
                          />
                        </td>
                        <td>
                          <button type="button" className="btn-delete" onClick={() => supprimerLigneCaisse(index)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {soldeDepartCaisse !== 0 && (
                      <tr className="totaux-row" style={{ opacity: 0.8 }}>
                        <td colSpan="4">
                          <em>Report de l'année précédente : {soldeDepartCaisse.toLocaleString('fr-FR')} FCFA</em>
                        </td>
                        <td></td>
                      </tr>
                    )}
                    <tr className="totaux-row">
                      <td><strong>TOTAL</strong></td>
                      <td><strong style={{color:'green'}}>{calculerTotauxCaisse().entrees.toLocaleString('fr-FR')} FCFA</strong></td>
                      <td><strong style={{color:'red'}}>{calculerTotauxCaisse().sorties.toLocaleString('fr-FR')} FCFA</strong></td>
                      <td colSpan="2">
                        <strong>Solde : {(soldeDepartCaisse + calculerTotauxCaisse().entrees - calculerTotauxCaisse().sorties).toLocaleString('fr-FR')} FCFA</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <button type="button" className="btn-add" onClick={ajouterLigneCaisse}>
              + Ajouter une ligne
            </button>

            {/* ── EMPRUNTS EN CAISSE ── */}
            <h2 style={{ marginTop: '30px' }}>Emprunts en Caisse</h2>
            <p className="section-hint">Emprunts contractés par les membres — à rembourser en dehors des cotisations courantes. Pré-rempli depuis le module Emprunts.</p>

            {formData.emprunts_en_cours.length > 0 && (
              <div className="table-responsive">
                <table className="cotisations-table">
                  <thead>
                    <tr>
                      <th>Membre</th>
                      <th>Montant emprunté (FCFA)</th>
                      <th>Montant remboursé (FCFA)</th>
                      <th>Reste à rembourser (FCFA)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.emprunts_en_cours.map((emprunt, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            value={emprunt.membre}
                            onChange={(e) => modifierEmprunt(index, 'membre', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            {membres.map(m => (
                              <option key={m.id} value={`${m.prenom} ${m.nom}`}>
                                {m.prenom} {m.nom}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="1"
                            value={emprunt.montant_session_24_25}
                            onChange={(e) => modifierEmprunt(index, 'montant_session_24_25', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="1"
                            value={emprunt.montant_session_25_26}
                            onChange={(e) => modifierEmprunt(index, 'montant_session_25_26', e.target.value)}
                          />
                        </td>
                        <td className="total-cell">
                          {(emprunt.total || 0).toLocaleString('fr-FR')} FCFA
                        </td>
                        <td>
                          <button type="button" className="btn-delete" onClick={() => supprimerEmprunt(index)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="totaux-row">
                      <td><strong>TOTAL EMPRUNTS</strong></td>
                      <td><strong>{formData.emprunts_en_cours.reduce((s, e) => s + (e.montant_session_24_25 || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      <td><strong>{formData.emprunts_en_cours.reduce((s, e) => s + (e.montant_session_25_26 || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      <td><strong style={{color:'red'}}>{formData.emprunts_en_cours.reduce((s, e) => s + (e.total || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <button type="button" className="btn-add" onClick={ajouterEmprunt}>
              + Ajouter un emprunt
            </button>

            {/* ── DETTE TOTALE ── */}
            <h2 style={{ marginTop: '30px' }}>Dette Totale de l'Association</h2>
            <p className="section-hint">Montant cumulé de toutes les dettes (cotisations, déuils, maladies, naissances).</p>
            <div className="form-row">
              <div className="form-group">
                <label>Dette totale cumulée (FCFA)</label>
                <input
                  type="number" min="0" step="1"
                  name="dette_totale"
                  value={formData.dette_totale || 0}
                  onChange={handleChange}
                  style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'red' }}
                />
              </div>
            </div>

            {/* ── COMPTES BANCAIRES ── */}
            <h2 style={{ marginTop: '30px' }}>Comptes Bancaires</h2>
            <p className="section-hint">Soldes des comptes d'épargne de l'association — pré-remplis depuis le module Comptes Bancaires.</p>

            {formData.releve_bancaire.length > 0 && (
              <div className="table-responsive">
                <table className="cotisations-table">
                  <thead>
                    <tr>
                      <th>Compte</th>
                      <th>Banque</th>
                      <th>Solde (FCFA)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.releve_bancaire.map((ligne, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={ligne.compte}
                            onChange={(e) => modifierLigneBancaire(index, 'compte', e.target.value)}
                            placeholder="Ex: Compte Épargne Principal"
                            style={{ width: '200px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ligne.banque}
                            onChange={(e) => modifierLigneBancaire(index, 'banque', e.target.value)}
                            placeholder="Ex: Afriland First Bank"
                            style={{ width: '160px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="1"
                            value={ligne.solde}
                            onChange={(e) => modifierLigneBancaire(index, 'solde', e.target.value)}
                          />
                        </td>
                        <td>
                          <button type="button" className="btn-delete" onClick={() => supprimerLigneBancaire(index)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="totaux-row">
                      <td colSpan="2"><strong>SOLDE BANCAIRE TOTAL</strong></td>
                      <td colSpan="2"><strong style={{color:'green'}}>{(formData.solde_banque || 0).toLocaleString('fr-FR')} FCFA</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <button type="button" className="btn-add" onClick={ajouterLigneBancaire}>
              + Ajouter un compte bancaire
            </button>

            {/* ── ÉVÉNEMENT EN PRÉPARATION ── */}
            {formData.evenement_cotisations.length > 0 && (
              <>
                <h2 style={{ marginTop: '30px' }}>
                  Événement{formData.evenement_nom ? ` — ${formData.evenement_nom}` : ''}
                </h2>
                <p className="section-hint">Cotisations événementielles par membre — pré-remplies depuis le module Événements.</p>

                <div className="table-responsive">
                  <table className="cotisations-table">
                    <thead>
                      <tr>
                        <th>Membre</th>
                        <th>Montant attendu (FCFA)</th>
                        <th>Montant payé (FCFA)</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.evenement_cotisations.map((ligne, index) => (
                        <tr key={index}>
                          <td>{ligne.membre}</td>
                          <td>{(ligne.montant_attendu || 0).toLocaleString('fr-FR')}</td>
                          <td>{(ligne.montant_paye || 0).toLocaleString('fr-FR')}</td>
                          <td>{ligne.statut}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="totaux-row">
                        <td><strong>TOTAL</strong></td>
                        <td><strong>{formData.evenement_cotisations.reduce((s, l) => s + (l.montant_attendu || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                        <td><strong style={{color:'green'}}>{formData.evenement_cotisations.reduce((s, l) => s + (l.montant_paye || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}

            <div className="form-actions final-actions">
              <button type="button" className="btn-secondary" onClick={() => setEtapeActive(5)}>
                Précédent
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={sauvegarderBrouillon}
                disabled={loading}
              >
                Sauvegarder comme brouillon
              </button>
              <button type="submit" className="btn-success" disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer et valider'}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default CompteRenduForm;
